/**
 * Create Pack Dialog
 *
 * Modal dialog for creating a new feature pack.
 * Slug is auto-generated from the display name.
 */

import { useState, type ReactElement, type FormEvent } from 'react';

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

interface CreatePackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string, displayName: string, description: string) => void;
  isSubmitting: boolean;
  error?: string | null;
}

export function CreatePackDialog({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  error,
}: CreatePackDialogProps): ReactElement | null {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');

  if (!open) return null;

  const slug = toSlug(displayName);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    onSubmit(slug, displayName, description);
  };

  return (
    <div className="dialog-overlay" onClick={onClose} role="presentation">
      <dialog
        className="dialog"
        open
        onClick={(e) => { e.stopPropagation(); }}
        aria-label="Create feature pack"
      >
        <h2>Create Feature Pack</h2>
        <form onSubmit={handleSubmit} className="dialog__form">
          <label className="dialog__field">
            Name
            <input value={displayName} onChange={(e) => { setDisplayName(e.target.value); }} required placeholder="e.g. Python Developer Pack" />
            {slug && <span className="dialog__hint">Slug: {slug}</span>}
          </label>
          <label className="dialog__field">
            Description
            <textarea value={description} onChange={(e) => { setDescription(e.target.value); }} rows={2} />
          </label>
          {error && <p className="dialog__error" style={{ color: 'var(--status-error, #e53e3e)', fontSize: '0.875rem', margin: 0 }}>{error}</p>}
          <div className="dialog__actions">
            <button type="button" onClick={onClose} className="dialog__btn dialog__btn--secondary">
              Cancel
            </button>
            <button type="submit" className="dialog__btn dialog__btn--primary" disabled={isSubmitting || slug.length < 2}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
